import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import getExternalMediaById from '@cartwhl/common/api/getExternalMediaById';
import getJobDownload, {
  jobZipDownloadFilename,
} from '@cartwhl/common/api/getJobDownload';
import getJobExportStatus from '@cartwhl/common/api/getJobExportStatus';
import { triggerBrowserFileDownload } from '@cartwhl/common/utils';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { userStore } from 'stores/global';

import BatchMascotVideoPreviewModal, {
  type MascotVideoPreviewNavigationJob,
} from '../../shared/BatchMascotVideoPreviewModal';
import BatchMotionJobPreviewModal, {
  type BatchPreviewNavigationJob,
  batchMotionFileCanPreview as fileCanPreview,
} from '../../shared/BatchMotionJobPreviewModal';
import {
  downloadCameraFbx,
  downloadMotionExportSlots,
  exportSlotsHaveReadyUrl,
} from '../../shared/batchMotionExportUrls';
import { useComicSourceVideoOverlay } from '../../shared/batchMotionPreviewPolling';
import { resolveDeletablePreviewAsset } from '../assetLibraryAssetLifecycle';
import {
  type AssetLibraryJobPreviewState,
  isComicMogenGroupID,
  motionRecordToPreviewFile,
  resolveMogenBatchExportId,
} from '../assetLibraryJobPreview';
import {
  buildAssetLibraryShareUrl,
  copyAssetLibraryShareLink,
} from '../assetLibraryShareLink';
import { useStore } from '../assetLibraryStore';
import { useAssetLibraryAssetActions } from '../hooks/useAssetLibraryAssetActions';
import type { AssetLibraryDisplayItem } from '../utils';
import {
  AssetLibraryPreviewBookmarkButton,
  AssetLibraryPreviewMoveToTrashButton,
  AssetLibraryPreviewShareLinkButton,
} from './AssetLibraryPreviewModalActions';

const ASSET_LIBRARY_JOB_PREVIEW_MODAL_MAX_WIDTH = 720;

function getPreviewJobNumber(
  jobId: string | null,
  navigationJobs: Array<{ id: string }>,
): number {
  if (!jobId) {
    return 1;
  }

  const index = navigationJobs.findIndex((job) => job.id === jobId);
  return index >= 0 ? index + 1 : 1;
}

function buildDisplayItemFromJobPreview(
  jobPreview: Pick<
    AssetLibraryJobPreviewState,
    'jobId' | 'groupID' | 'assetType' | 'isBookmarked'
  >,
): AssetLibraryDisplayItem | null {
  if (!jobPreview.jobId || !jobPreview.groupID || !jobPreview.assetType) {
    return null;
  }

  return {
    assetID: jobPreview.jobId,
    assetName: jobPreview.jobId,
    assetType: jobPreview.assetType,
    groupID: jobPreview.groupID,
    createdAt: new Date(0).toISOString(),
    createdBy: '',
    createdByUser: '',
    lastUpdatedAt: new Date(0).toISOString(),
    thumbnailURL: null,
    isBookmarked: jobPreview.isBookmarked,
  };
}

export default function AssetLibraryJobPreviewModals() {
  const store = useStore();
  const { jobPreview } = store.state;
  const { toggleBookmark, requestMoveToTrash, canDeleteAsset } =
    useAssetLibraryAssetActions();
  const { apiKey: apiKeyState } = useSnapshot(userStore.state);
  const apiKey = apiKeyState.data?.apiKey ?? '';

  const previewFiles = useMemo(
    () => jobPreview.motions.map(motionRecordToPreviewFile),
    [jobPreview.motions],
  );

  const previewFile = useMemo(
    () =>
      jobPreview.jobId
        ? previewFiles.find((file) => file.id === jobPreview.jobId)
        : undefined,
    [jobPreview.jobId, previewFiles],
  );

  const previewNavJobs = useMemo<BatchPreviewNavigationJob[]>(
    () =>
      previewFiles
        .filter((file) => fileCanPreview(file))
        .map((file, index) => ({
          id: file.id,
          name: file.name,
          batchMotionNumber: index + 1,
          canPreview: true,
        })),
    [previewFiles],
  );

  const batchMotionNumberForPreview = useMemo(
    () => getPreviewJobNumber(jobPreview.jobId, previewNavJobs),
    [jobPreview.jobId, previewNavJobs],
  );

  const mascotPreviewFile = useMemo(
    () =>
      jobPreview.jobId
        ? jobPreview.mascots.find(
            (mascot) => mascot.mascotID === jobPreview.jobId,
          )
        : undefined,
    [jobPreview.jobId, jobPreview.mascots],
  );

  const mascotPreviewNavigationJobs = useMemo<
    MascotVideoPreviewNavigationJob[]
  >(
    () =>
      jobPreview.mascots
        .filter(
          (mascot) =>
            mascot.status === 'COMPLETED' && Boolean(mascot.outputVideoURL),
        )
        .map((mascot, index) => ({
          id: mascot.mascotID,
          name: mascot.mascotName ?? mascot.mascotID,
          jobNumber: index + 1,
        })),
    [jobPreview.mascots],
  );

  const mascotPreviewJobNumber = useMemo(
    () => getPreviewJobNumber(jobPreview.jobId, mascotPreviewNavigationJobs),
    [jobPreview.jobId, mascotPreviewNavigationJobs],
  );

  const getSourceVideoMedia = useCallback(
    async (mediaId: string) => {
      const { mediaDownloadURL, fileExtension } = await getExternalMediaById(
        mediaId,
        apiKey,
      );
      return { url: mediaDownloadURL, fileExtension };
    },
    [apiKey],
  );

  const comicSourceOverlay = useComicSourceVideoOverlay(
    getSourceVideoMedia,
    jobPreview.groupID && isComicMogenGroupID(jobPreview.groupID)
      ? previewFile?.sourceVideoMediaID
      : undefined,
  );

  const pollJobZipExportReadiness = useCallback(() => {
    if (
      !jobPreview.jobId ||
      !jobPreview.groupID ||
      jobPreview.assetType !== 'MOGEN_JOB' ||
      !apiKey
    ) {
      return Promise.resolve({
        downloadExportsReady: false,
        hasFailedExport: false,
        exportRequestCount: 0,
        expectedExportRequests: 0,
        completedExportRequests: 0,
      });
    }

    const batchID = resolveMogenBatchExportId(jobPreview.groupID);
    return getJobExportStatus(batchID, jobPreview.jobId, apiKey);
  }, [apiKey, jobPreview.assetType, jobPreview.groupID, jobPreview.jobId]);

  const handleMogenDownload = useCallback(
    async (opts?: { bodyIndex?: number; camera?: boolean }) => {
      if (!previewFile || !jobPreview.groupID || !apiKey) {
        return;
      }

      const batchID = resolveMogenBatchExportId(jobPreview.groupID);
      const exportURLs = previewFile.exportURLs;
      const cameraFbxURL = previewFile.cameraFbxURL?.trim();

      if (opts?.camera) {
        if (!cameraFbxURL) {
          return;
        }
        await downloadCameraFbx({
          cameraFbxURL,
          displayName: previewFile.name,
          exportFilenames: previewFile.exportFilenames,
        });
        return;
      }

      if (exportURLs?.length && exportSlotsHaveReadyUrl(exportURLs)) {
        await downloadMotionExportSlots({
          exportURLs,
          exportFilenames: previewFile.exportFilenames,
          displayName: previewFile.name,
          exportType: jobPreview.batchExportSettings?.exportType,
          bodyIndex: opts?.bodyIndex,
        });
        return;
      }

      const { downloadURL } = await getJobDownload(
        batchID,
        previewFile.id,
        apiKey,
        opts?.bodyIndex !== undefined
          ? { bodyIndex: opts.bodyIndex }
          : undefined,
      );
      const filename = jobZipDownloadFilename(
        previewFile.name,
        previewFile.id,
        opts?.bodyIndex,
      );
      triggerBrowserFileDownload(downloadURL, filename);
    },
    [
      apiKey,
      jobPreview.batchExportSettings?.exportType,
      jobPreview.groupID,
      previewFile,
    ],
  );

  const handleMascotDownload = useCallback(() => {
    const url = mascotPreviewFile?.outputVideoURL;
    if (!url) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${mascotPreviewFile?.mascotName ?? mascotPreviewFile?.mascotID ?? 'mascot'}.mp4`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [mascotPreviewFile]);

  const isProjectPreview = jobPreview.groupKind === 'project';
  const isJobPreviewOpen = Boolean(jobPreview.jobId);
  const showMainLibraryPreviewActions =
    jobPreview.showMainLibraryPreviewActions;
  const previewAsset = buildDisplayItemFromJobPreview(jobPreview);
  const shareUrl = previewAsset
    ? buildAssetLibraryShareUrl(previewAsset)
    : null;
  const canShareJobPreview =
    Boolean(shareUrl) && store.state.libraryView !== 'trash';
  const deletableAsset = resolveDeletablePreviewAsset(
    previewAsset,
    store.state,
  );
  const moveToTrashDisabled = !canDeleteAsset(deletableAsset);
  const previewTitleTrailing =
    canShareJobPreview || showMainLibraryPreviewActions ? (
      <div className="flex shrink-0 items-center gap-2">
        {canShareJobPreview ? (
          <AssetLibraryPreviewShareLinkButton
            onClick={() => {
              void copyAssetLibraryShareLink(shareUrl!);
            }}
          />
        ) : null}
        {showMainLibraryPreviewActions ? (
          <AssetLibraryPreviewBookmarkButton
            isBookmarked={jobPreview.isBookmarked}
            onClick={
              previewAsset
                ? () => {
                    void toggleBookmark(previewAsset);
                  }
                : undefined
            }
          />
        ) : null}
      </div>
    ) : null;
  const previewFooterButtons = showMainLibraryPreviewActions ? (
    <AssetLibraryPreviewMoveToTrashButton
      disabled={moveToTrashDisabled}
      onClick={
        previewAsset ? () => requestMoveToTrash(previewAsset) : undefined
      }
    />
  ) : null;

  if (isJobPreviewOpen && jobPreview.status === 'pending') {
    return (
      <Modal
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            store.closeJobPreview();
          }
        }}
        title="Loading preview…"
        maxWidth={ASSET_LIBRARY_JOB_PREVIEW_MODAL_MAX_WIDTH}
        fullscreenOnMobile
      >
        <div className="flex justify-center p-12">
          <Loading />
        </div>
      </Modal>
    );
  }

  if (isJobPreviewOpen && jobPreview.status === 'error') {
    return (
      <Modal
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            store.closeJobPreview();
          }
        }}
        title="Unable to load preview"
        maxWidth={480}
        footer={
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => store.closeJobPreview()}
          >
            Close
          </Button>
        }
      >
        <Text token="14/medium" className="text-secondary text-pretty">
          {jobPreview.error ??
            'Something went wrong while loading this preview.'}
        </Text>
      </Modal>
    );
  }

  if (
    jobPreview.assetType === 'MOGEN_JOB' &&
    previewFile &&
    fileCanPreview(previewFile)
  ) {
    return (
      <BatchMotionJobPreviewModal
        open={Boolean(jobPreview.jobId)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            store.closeJobPreview();
          }
        }}
        jobId={previewFile.id}
        jobTitle={previewFile.name}
        batchMotionNumber={batchMotionNumberForPreview}
        totalMotionsInBatch={previewNavJobs.length}
        exportURLs={isProjectPreview ? undefined : previewFile.exportURLs}
        exportFilenames={
          isProjectPreview ? undefined : previewFile.exportFilenames
        }
        bvhURL={previewFile.bvhURL ?? ''}
        bvhURLs={previewFile.bvhURLs}
        mhrBvhURLs={previewFile.mhrBvhURLs}
        mhrIdentityJsonURLs={previewFile.mhrIdentityJsonURLs}
        cameraFbxURL={isProjectPreview ? undefined : previewFile.cameraFbxURL}
        exportSettings={jobPreview.batchExportSettings}
        hideDownloadUI={isProjectPreview}
        onDownload={
          isProjectPreview
            ? undefined
            : (opts) => void handleMogenDownload(opts)
        }
        pollJobZipExportReadiness={
          isProjectPreview ? undefined : pollJobZipExportReadiness
        }
        previewNavigation={{
          jobs: previewNavJobs,
          onSelectJobId: (jobId) => store.setPreviewJobId(jobId),
        }}
        sourceVideoOverlayUrl={comicSourceOverlay?.url}
        sourceVideoFileExtension={comicSourceOverlay?.fileExtension}
        modalMaxWidth={ASSET_LIBRARY_JOB_PREVIEW_MODAL_MAX_WIDTH}
        titleTrailing={previewTitleTrailing}
        footerLeadingExtra={previewFooterButtons}
      />
    );
  }

  if (
    jobPreview.assetType === 'MASCOT_JOB' &&
    mascotPreviewFile?.outputVideoURL
  ) {
    return (
      <BatchMascotVideoPreviewModal
        open={Boolean(jobPreview.jobId)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            store.closeJobPreview();
          }
        }}
        jobId={mascotPreviewFile.mascotID}
        jobTitle={mascotPreviewFile.mascotName ?? mascotPreviewFile.mascotID}
        jobNumber={mascotPreviewJobNumber}
        totalJobs={mascotPreviewNavigationJobs.length}
        videoUrl={mascotPreviewFile.outputVideoURL}
        onDownload={handleMascotDownload}
        navigation={{
          jobs: mascotPreviewNavigationJobs,
          onSelectJobId: (jobId) => store.setPreviewJobId(jobId),
        }}
        modalMaxWidth={ASSET_LIBRARY_JOB_PREVIEW_MODAL_MAX_WIDTH}
        titleTrailing={previewTitleTrailing}
        footerLeadingExtra={previewFooterButtons}
      />
    );
  }

  return null;
}
